import React, { useState } from 'react';
import { Button, Card, Form, Input, Typography } from 'antd';
import { toast } from '../../../lib/toast';
import { changePasswordApi } from '../api/auth';
import { useAuthStore } from '../store/authStore';

const { Text } = Typography;

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ChangePasswordCardProps {
  disabled?: boolean;
}

const ChangePasswordCard: React.FC<ChangePasswordCardProps> = ({ disabled = false }) => {
  const [loading, setLoading] = useState(false);
  const setToken = useAuthStore((s) => s.setToken);
  const setCsrfToken = useAuthStore((s) => s.setCsrfToken);

  const onFinish = async (values: FormValues) => {
    setLoading(true);
    try {
      const result = await changePasswordApi({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      if (result.success) {
        setToken(result.accessToken);
        setCsrfToken(result.csrfToken);
        toast.success('Password updated. Other devices have been signed out.');
      } else {
        toast.error(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Change password" style={{ marginBottom: 24 }}>
      {disabled ? (
        <Text type="secondary">Complete account setup using your invite link before changing your password.</Text>
      ) : (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            You will stay signed in on this device. All other sessions will be signed out.
          </Text>
          <Form layout="vertical" onFinish={onFinish} autoComplete="off">
            <Form.Item
              label="Current password"
              name="currentPassword"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              label="New password"
              name="newPassword"
              rules={[
                { required: true, message: 'Required' },
                { min: 8, message: 'At least 8 characters' },
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              label="Confirm new password"
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Update password
            </Button>
          </Form>
        </>
      )}
    </Card>
  );
};

export default ChangePasswordCard;
